// src/preview/timeline.ts
// When and where everything in a spec appears, in the draft renderer's
// terms: the arithmetic of make_video's build_command / build_background
// filtergraph, without the DOM, so it can be unit tested. stage.ts draws it.
// ─────────────────────────────────────────────────────────────────

import { DEFAULT_IMAGE_SCALE, GRID, beatBoxes, endTextBeat, imageBox, pyRound, type Json } from './spec';

/** Seconds of make_video's end-card (and end-card text) fade-in. */
export const END_FADE = 0.4;

export interface Window {
  /**
   * Seconds from the start of the video. Shown for start <= t < end: ffmpeg
   * feeds each overlay as end - start seconds of frames, so its last frame
   * is one frame before `end` (the end card's window ends with the video).
   */
  start: number;
  end: number;
  fadeIn: number;
  fadeOut: number;
}

export interface Timeline {
  beatStarts: number[];
  beatsTotal: number;
  /** Beats plus the end card. */
  total: number;
  hasEnd: boolean;
}

export function timeline(spec: Json): Timeline {
  const beatStarts: number[] = [];
  let t = 0;
  for (const b of spec.beats as Json[]) {
    beatStarts.push(t);
    t += b.duration;
  }
  const hasEnd = Boolean(spec.endCard?.image);
  return { beatStarts, beatsTotal: t, total: t + (hasEnd ? spec.endCard.duration : 0), hasEnd };
}

/**
 * make_video's window() + fades(): an item appears `start` seconds into its
 * beat and lasts until the beat ends. Fade length is capped at half the
 * time shown; something visible from frame 0 doesn't fade in.
 */
export function itemWindow(tl: Timeline, spec: Json, beatIndex: number, item: Json): Window {
  const s = tl.beatStarts[beatIndex] + (item.start ?? 0);
  const e = tl.beatStarts[beatIndex] + spec.beats[beatIndex].duration;
  const f = Math.min(spec.style.fade, (e - s) / 2);
  return { start: s, end: e, fadeIn: f > 0 && s > 0 ? f : 0, fadeOut: f > 0 ? f : 0 };
}

/** The end card (and its text) from the end of the beats to the end of the video, fading in only. */
export function endWindow(tl: Timeline): Window {
  return { start: tl.beatsTotal, end: tl.total, fadeIn: END_FADE, fadeOut: 0 };
}

/**
 * A background overlay: the whole video, or (`during: beats`, with an end
 * card) fading out over the end card's fade-in, gone after it.
 */
export function overlayWindow(tl: Timeline, overlay: Json): Window {
  if (overlay.during === 'beats' && tl.hasEnd) {
    // may end after the video does (an end card shorter than the fade), which cuts the fade short as ffmpeg does
    return { start: 0, end: tl.beatsTotal + END_FADE, fadeIn: 0, fadeOut: END_FADE };
  }
  return { start: 0, end: tl.total, fadeIn: 0, fadeOut: 0 };
}

/** Opacity of something with window `w` at time t (0 when hidden). Fades are linear, like ffmpeg's fade filter. */
export function alphaAt(w: Window, t: number): number {
  if (t < w.start || t >= w.end) return 0;
  let a = 1;
  if (w.fadeIn > 0) a = Math.min(a, (t - w.start) / w.fadeIn);
  if (w.fadeOut > 0) a = Math.min(a, (w.end - t) / w.fadeOut);
  return Math.max(0, Math.min(1, a));
}

export interface TextItem { beat: number; box: Json; window: Window }
export interface ImageItem { beat: number; image: Json; window: Window }

/** Text boxes worth drawing (empty ones are just a pause on the background), with their windows. */
export function textItems(spec: Json, tl: Timeline): TextItem[] {
  return (spec.beats as Json[]).flatMap((b, i) => beatBoxes(b)
    .filter(box => typeof box.text === 'string' && box.text.trim())
    .map(box => ({ beat: i, box, window: itemWindow(tl, spec, i, box) })));
}

export function imageItems(spec: Json, tl: Timeline): ImageItem[] {
  return (spec.beats as Json[]).flatMap((b, i) => (Array.isArray(b.images) ? b.images as Json[] : [])
    .map(image => ({ beat: i, image, window: itemWindow(tl, spec, i, image) })));
}

export function endText(spec: Json): Json | null {
  const et = endTextBeat(spec);
  return et && typeof et.text === 'string' && et.text.trim() ? et : null;
}

// ── geometry ─────────────────────────────────────────────────────

export interface Rect { x: number; y: number; w: number; h: number }

/** Zoom level at time t: make_video's zoom_expr. */
export function zoomAt(zoom: Json, total: number, t: number): number {
  const z0: number = zoom.from ?? 1.0, z1: number = zoom.to ?? 1.25;
  let p = Math.min(1, t / total);
  if (zoom.ease === 'ease-out') p = 1 - (1 - p) ** 2;
  return z0 + (z1 - z0) * p;
}

/**
 * The part of a source (sw x sh) that fills a w x h frame at time t: scale
 * to cover and centre-crop, then zoom in keeping the grid point pinned
 * (zoompan's x = ax*(iw - iw/zoom)). Video backgrounds use the same maths
 * (scale up by zoom, crop at ax*(iw - w)).
 */
export function backgroundSourceRect(sw: number, sh: number, w: number, h: number, zoom: Json, total: number, t: number): Rect {
  const [ax, ay] = GRID[zoom.target] ?? GRID.center;
  const cover = Math.max(w / sw, h / sh);
  const cw = w / cover, ch = h / cover; // the covered frame, in source px
  const z = zoomAt(zoom, total, t);
  const vw = cw / z, vh = ch / z;
  return { x: (sw - cw) / 2 + ax * (cw - vw), y: (sh - ch) / 2 + ay * (ch - vh), w: vw, h: vh };
}

/** Source rect that centre-crops a sw x sh source to cover a w x h frame (scale ...increase, crop). */
export function coverRect(sw: number, sh: number, w: number, h: number): Rect {
  const cover = Math.max(w / sw, h / sh);
  const cw = w / cover, ch = h / cover;
  return { x: (sw - cw) / 2, y: (sh - ch) / 2, w: cw, h: ch };
}

/** Size of a sw x sh source scaled to fit inside boxW x boxH (scale ...decrease); may scale up. */
export function containSize(sw: number, sh: number, boxW: number, boxH: number): [number, number] {
  const k = Math.min(boxW / sw, boxH / sh);
  return [Math.max(1, Math.round(sw * k)), Math.max(1, Math.round(sh * k))];
}

/**
 * make_video's pin_expr: one axis of an overlay's position, its point `a`
 * (0 = start edge, 0.5 = middle, 1 = end edge) at pct of the canvas `size`,
 * kept on-canvas. `own` is the overlay's size on that axis.
 */
export function pin(size: number, own: number, pct: number, a: number): number {
  return Math.floor(Math.max(0, Math.min(size - own, pyRound(size * pct / 100) - a * own)));
}

/** make_video's overlay_y: the `anchor` edge of an oh-tall overlay at yPct of the canvas, kept on-canvas. */
export function overlayY(h: number, oh: number, yPct: number, anchor = 'middle'): number {
  return pin(h, oh, yPct, anchor === 'top' ? 0 : anchor === 'bottom' ? 1 : 0.5);
}

/** Where a beats[].images[] entry of size iw x ih (already its own size for Kindle pages) is drawn. */
export function imageRect(iw: number, ih: number, image: Json, w: number, h: number): Rect {
  const scale: number = image.scale ?? DEFAULT_IMAGE_SCALE;
  const [ow, oh] = containSize(iw, ih, pyRound(w * scale), pyRound(h * scale * 0.85));
  return { x: Math.floor((w - ow) / 2), y: overlayY(h, oh, image.y ?? 50, image.anchor ?? 'middle'), w: ow, h: oh };
}

/**
 * Where a background overlay of size iw x ih is drawn: make_video's
 * image_box + overlay_xy, its `anchor` point (a GRID name) at (x, y)% of the canvas,
 * kept on-canvas.
 */
export function overlayRect(iw: number, ih: number, overlay: Json, w: number, h: number): Rect {
  const [ow, oh] = containSize(iw, ih, ...imageBox(w, h, overlay.scale));
  const [ax, ay] = GRID[overlay.anchor] ?? GRID['upper-left'];
  return { x: pin(w, ow, overlay.x, ax), y: pin(h, oh, overlay.y, ay), w: ow, h: oh };
}

/** Where the end-card image goes in overlay mode and fullscreen contain mode. */
export function endImageRect(iw: number, ih: number, end: Json, w: number, h: number): Rect {
  const [ow, oh] = end.mode === 'fullscreen'
    ? containSize(iw, ih, w, h)
    : containSize(iw, ih, pyRound(w * end.scale), pyRound(h * end.scale * 0.85));
  return { x: Math.floor((w - ow) / 2), y: overlayY(h, oh, end.y), w: ow, h: oh };
}

/** Audio gain at time t: afade in from 0 and out to the end, linear (ffmpeg's default curve). */
export function audioGainAt(audio: Json, total: number, t: number): number {
  let g = 1;
  if (audio.fadeIn > 0) g = Math.min(g, t / audio.fadeIn);
  if (audio.fadeOut > 0) g = Math.min(g, (total - t) / audio.fadeOut);
  return Math.max(0, Math.min(1, g));
}

/** Canvas size for a tier: make_video's preview shrink (each side halved and kept even, for shrink 2). */
export function stageSize(size: [number, number], shrink: number): [number, number] {
  if (shrink <= 1) return size;
  const k = 2 * shrink;
  return [Math.max(2, Math.floor(size[0] / k) * 2), Math.max(2, Math.floor(size[1] / k) * 2)];
}
